import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api/client';

const steps = ['Dane firmy', 'Struktura', 'Pracownicy', 'Branding', 'Podsumowanie'];

const PLANS = [
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

export default function ConsoleClientOnboarding() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    shortName: '',
    domain: '',
    planId: 'starter',
    adminEmail: '',
    departments: [''],
    employeesRaw: '',
    primaryColor: '#6D55F7',
    accentColor: '#9DE94B',
    fontFamily: 'Manrope',
  });

  const [createdTenantId, setCreatedTenantId] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const parseEmployees = () => {
    const lines = form.employeesRaw.split('\n').filter(Boolean);
    return lines.map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      const email = parts.find((p) => p.includes('@')) || line.trim();
      const displayName = parts.find((p) => !p.includes('@')) || email.split('@')[0];
      return { email, displayName };
    });
  };

  const handleNext = async () => {
    setError('');
    if (activeStep === steps.length - 1) {
      navigate('/console/clients');
      return;
    }

    if (activeStep === 0) {
      if (!form.name || !form.domain || !form.adminEmail) {
        setError('Wypełnij nazwę, domenę i e-mail administratora');
        return;
      }
      setLoading(true);
      try {
        const res = await api.post('/api/tenants', {
          name: form.name,
          shortName: form.shortName,
          domain: form.domain,
          planId: form.planId,
          adminEmail: form.adminEmail,
          branding: {
            primaryColor: form.primaryColor,
            accentColor: form.accentColor,
            fontFamily: form.fontFamily,
          },
        });
        setCreatedTenantId(res.id);
        await applySettings(res.id);
        setActiveStep((s) => s + 1);
      } catch (err) {
        setError(err.body?.error || err.message || 'Błąd tworzenia klienta');
      } finally {
        setLoading(false);
      }
    } else if (activeStep === 3) {
      setLoading(true);
      try {
        await applySettings(createdTenantId);
        setActiveStep((s) => s + 1);
      } catch (err) {
        setError(err.body?.error || err.message || 'Błąd zapisu brandingu');
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const applySettings = async (tenantId) => {
    const headers = { 'X-Tenant-ID': tenantId };

    // Save branding
    await api.put(
      '/api/tenants/me',
      {
        branding: {
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          fontFamily: form.fontFamily,
        },
        config: {
          structure: { departments: form.departments.filter(Boolean) },
        },
      },
      { headers }
    );

    // Bulk import employees
    const employees = parseEmployees();
    if (employees.length > 0) {
      await api.post('/api/tenants/me/users/bulk', { users: employees }, { headers });
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const updateDepartment = (idx, value) => {
    const next = [...form.departments];
    next[idx] = value;
    setForm((f) => ({ ...f, departments: next }));
  };

  const addDepartment = () => setForm((f) => ({ ...f, departments: [...f.departments, ''] }));

  const removeDepartment = (idx) => {
    setForm((f) => ({ ...f, departments: f.departments.filter((_, i) => i !== idx) }));
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Pełna nazwa firmy"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Nazwa skrócona"
              value={form.shortName}
              onChange={(e) => update('shortName', e.target.value)}
              fullWidth
              helperText="np. Żabka, Rossmann"
            />
            <TextField
              label="Domena / subdomena"
              value={form.domain}
              onChange={(e) => update('domain', e.target.value)}
              fullWidth
              required
              helperText="np. zabka → zabka.challenges.app"
            />
            <FormControl fullWidth required>
              <InputLabel>Plan</InputLabel>
              <Select value={form.planId} label="Plan" onChange={(e) => update('planId', e.target.value)}>
                {PLANS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="E-mail administratora"
              type="email"
              value={form.adminEmail}
              onChange={(e) => update('adminEmail', e.target.value)}
              fullWidth
              required
              helperText="Pierwszy użytkownik z rolą admin"
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Dodaj działy / strukturę organizacyjną klienta.
            </Typography>
            {form.departments.map((dept, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label={`Dział ${idx + 1}`}
                  value={dept}
                  onChange={(e) => updateDepartment(idx, e.target.value)}
                  fullWidth
                />
                <IconButton onClick={() => removeDepartment(idx)} color="error" disabled={form.departments.length === 1}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addDepartment} sx={{ mt: 1 }}>
              Dodaj dział
            </Button>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Wklej listę pracowników — każdy w nowej linii. Format: <code>imię nazwisko, e-mail</code>.
            </Typography>
            <TextField
              label="Pracownicy"
              value={form.employeesRaw}
              onChange={(e) => update('employeesRaw', e.target.value)}
              multiline
              rows={8}
              fullWidth
              placeholder={`Jan Kowalski, jan.kowalski@firma.pl\nAnna Nowak, anna.nowak@firma.pl`}
            />
            <Chip label={`Liczba: ${parseEmployees().length}`} size="small" />
          </Box>
        );
      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Kolor główny"
              type="color"
              value={form.primaryColor}
              onChange={(e) => update('primaryColor', e.target.value)}
              fullWidth
            />
            <TextField
              label="Kolor akcentujący"
              type="color"
              value={form.accentColor}
              onChange={(e) => update('accentColor', e.target.value)}
              fullWidth
            />
            <TextField
              label="Rodzina czcionek"
              value={form.fontFamily}
              onChange={(e) => update('fontFamily', e.target.value)}
              fullWidth
            />
          </Box>
        );
      case 4:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Tenant <strong>{form.name}</strong> został utworzony.
            </Alert>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px` }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Podsumowanie
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 1, rowGap: 1 }}>
                <Typography color="text.secondary">Nazwa</Typography>
                <Typography>{form.name}</Typography>
                <Typography color="text.secondary">Nazwa skrócona</Typography>
                <Typography>{form.shortName || '—'}</Typography>
                <Typography color="text.secondary">Domena</Typography>
                <Typography>{form.domain}.challenges.app</Typography>
                <Typography color="text.secondary">Plan</Typography>
                <Typography>{form.planId}</Typography>
                <Typography color="text.secondary">Działy</Typography>
                <Typography>{form.departments.filter(Boolean).join(', ') || '—'}</Typography>
                <Typography color="text.secondary">Pracownicy</Typography>
                <Typography>{parseEmployees().length}</Typography>
                <Typography color="text.secondary">Admin</Typography>
                <Typography>{form.adminEmail}</Typography>
              </Box>
            </Paper>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Nowy klient
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px` }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 240 }}>{renderStep()}</Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
            Wstecz
          </Button>
          <Button variant="contained" onClick={handleNext} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : activeStep === steps.length - 1 ? 'Zakończ' : 'Dalej'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
